import { Sheet } from "./ux/Sheet";

/**
 * "What is a kiki?" explainer — a dictionary-style definition plus how it
 * works. Shared by the event page's kiki strip and the Kiki carousel header.
 * Renders through the shared Sheet so it animates (slide-up on mobile, scale-in
 * popup on desktop) and gets backdrop / Escape / scroll-lock for free.
 */
export function KikiHelpModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Sheet open={isOpen} onClose={onClose} hideHeader className="sm:max-w-sm">
      <div className="space-y-5">
        <div>
          <p className="text-xs font-semibold tracking-widest uppercase text-neutral-400 dark:text-neutral-500 mb-2">
            ki·ki
          </p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 italic mb-2">
            /ˈkiːkiː/ · noun · informal
          </p>
          <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
            A casual gathering to catch up, share a little gossip, and just feel connected. The kind of hangout where everyone leaves feeling a bit more themselves.
          </p>
        </div>

        <div className="border-t border-neutral-200 dark:border-neutral-800" />

        <div className="space-y-3">
          <h3 className="text-base font-semibold text-neutral-900 dark:text-white">
            Don't want to go alone? You don't have to.
          </h3>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
            Starting a kiki lets you find others for the same plan, whether it's an event you're eyeing or your own idea. Connect beforehand, and show up as a little crew instead of flying solo.
          </p>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
            Turn a room full of strangers into a room with at least one familiar face.
          </p>
        </div>

        <div className="border-t border-neutral-200 dark:border-neutral-800" />

        <ol className="space-y-3">
          {[
            "Start a kiki: tie it to an event or create your own plan, with a short note about who you'd love to go with.",
            "Others into it can reach out. You'll see who's interested and pick the vibe.",
            "Chat ahead of time, meet up, and show up together.",
          ].map((text, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-primary-500/15 text-primary-500 text-xs font-semibold flex items-center justify-center">
                {i + 1}
              </span>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{text}</p>
            </li>
          ))}
        </ol>

        <button type="button" onClick={onClose} className="btn btn-primary w-full mt-1">
          Got it
        </button>
      </div>
    </Sheet>
  );
}

export default KikiHelpModal;
